import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import InfoButton from '../../common/InfoButton'
import ExpandButton from '../../common/ExpandButton'
import { useTourContext } from '../../../context/TourContext'
import { counsellorWellbeingData } from '../../../constants/CounsellorAnalyticsConstants'
import { ProgressBarComfortable } from '../class/ClassEmotionalWellbeing'
import { calculateCoefficient } from '../../../utils/util'
import { LoadingContainer } from '../student/CheckInWidget'
import { loadCounsellorWellbeingComparison, loadOrganizationWellbeingComparison } from '../../../server/server'

// SVG Icons
import { ReactComponent as BigArrow } from '../../../assets/svg/BigArrow.svg'
import { ReactComponent as UserCardId } from '../../../assets/svg/UserCardId.svg'
import { ReactComponent as ChevronDownDuoIcon } from '../../../assets/svg/ChevronDownDuoIcon.svg'
import { ReactComponent as SchoolAverageIcon } from '../../../assets/svg/SchoolAverageIcon.svg'

// Constants
const SHOW_ROWS = 5
const STEP = 10
const TREND_THRESHOLD = 0.1

// Utility functions
const calculatePercentage = (value, total) => {
    return total !== 0 ? Math.round((value / total) * 100) : 0
}

const calculateTrendComparison = (current, reference, threshold = TREND_THRESHOLD) => {
    const difference = current - reference
    const sameTrend = Math.abs(difference) < threshold
    const positiveTrend = current >= reference

    return {
        sameTrend,
        positiveTrend,
        difference,
        label: sameTrend ? 'same as average' : `${positiveTrend ? '+' : ''}${Math.round(difference)}% ${positiveTrend ? 'over' : 'below'} average`,
    }
}

const getLoadFunction = (analyticsType) => {
    switch (analyticsType) {
        case 'organization':
            return loadOrganizationWellbeingComparison
        case 'counsellor':
        default:
            return loadCounsellorWellbeingComparison
    }
}

// Custom hooks
const useWellbeingData = (selectedUsersScope, daysFilter, analyticsType) => {
    const [loading, setLoading] = useState(false)
    const [data, setData] = useState(counsellorWellbeingData)
    const [error, setError] = useState(null)

    const type = analyticsType || 'counsellor'

    const loadData = useCallback(async () => {
        try {
            setLoading(true)
            setError(null)

            const loadFunction = getLoadFunction(analyticsType)
            const response = await loadFunction(selectedUsersScope, daysFilter.startDate, daysFilter.endDate)

            if (response.status === 200) {
                setData(response.data)
            } else {
                setError('Failed to load wellbeing data')
            }
        } catch (err) {
            setError(err.message || 'An error occurred while loading data')
        } finally {
            setLoading(false)
        }
    }, [selectedUsersScope, daysFilter.numberOfDays, analyticsType])

    useEffect(() => {
        loadData()
    }, [loadData])

    return { loading, data, error, refetch: loadData, type }
}

const usePagination = (data, type) => {
    const [limit, setLimit] = useState(SHOW_ROWS)

    const dataKey = type === 'counsellor' ? 'years' : 'institutions'
    const totalItems = data?.[dataKey]?.length || 0
    const hasMore = totalItems > limit

    const handleShowMore = useCallback(() => {
        setLimit((prev) => (prev > SHOW_ROWS ? SHOW_ROWS : totalItems))
    }, [totalItems])

    const resetPagination = useCallback(() => {
        setLimit(SHOW_ROWS)
    }, [])

    return {
        limit,
        hasMore,
        handleShowMore,
        resetPagination,
        totalItems,
    }
}

// Header Components
const WellbeingHeader = React.memo(({ daysFilter, t }) => (
    <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-center gap-2">
            <p className="text-[18px] leading-[25px] font-medium text-darkenPurple">{t('Wellbeing comparison for selected student groups')}</p>
            <div className="flex items-center justify-center rounded-full bg-[#413F5E14] h-6 px-[10px]">
                <p className="text-[13px] leading-[18px] font-normal text-strongGray">{t(daysFilter.label)}</p>
            </div>
        </div>
        <div className="flex items-center gap-3">
            <button>
                <InfoButton color="#5E639B" text={t('Compare the wellbeing of different groups against the school average to spot trends and outliers.')} />
            </button>
        </div>
    </div>
))

const ShowMoreButton = React.memo(({ loading, hasMore, limit, isOrganization, onShowMore, t }) => {
    if (loading || !hasMore) return null

    const isExpanded = limit > SHOW_ROWS

    return (
        <div className="flex flex-row justify-end w-full mt-4">
            <button
                className="flex flex-row items-center justify-center gap-2 border border-[#ECEBF8] hover:border-deepPurple transition rounded-full h-[34px] px-4 bg-[#F8F8FB]"
                onClick={onShowMore}
                aria-label={isExpanded ? 'Show less items' : 'Show all items'}
            >
                <p className="text-deepPurple text-[16px] leading-5 font-semibold">
                    {isExpanded ? (isOrganization ? t('See less organizations') : t('See less years')) : isOrganization ? t('See all organizations') : t('See all years')}
                </p>
                <div className={`${isExpanded ? 'rotate-180' : 'rotate-0'} transition`}>
                    <ChevronDownDuoIcon color="#5E639B" />
                </div>
            </button>
        </div>
    )
})

/**
 * WellbeingComparisonWidget Component
 * @param {Object} props
 * @param {Object} props.daysFilter - Filter for days
 * @param {string} props.selectedUsersScope - Selected user scope
 * @param {string} props.analyticsType - Type of analytics ('counsellor' | 'organization')
 */
function WellbeingComparisonWidget({ daysFilter, selectedUsersScope, analyticsType }) {
    const { t } = useTranslation()
    const { loading, data, error, type } = useWellbeingData(selectedUsersScope, daysFilter, analyticsType)
    const { limit, hasMore, handleShowMore } = usePagination(data, type)

    const isOrganization = type === 'organization'
    const showLoading = useMemo(() => loading || !data, [loading, data])

    if (error) {
        return (
            <div className="bg-white rounded-[24px] p-6 mt-4">
                <div className="text-center text-red-500">
                    <p>Error loading wellbeing data: {error}</p>
                </div>
            </div>
        )
    }

    return (
        <div className="bg-white rounded-[24px] p-6 mt-4" data-tour="alerts">
            <WellbeingHeader daysFilter={daysFilter} t={t} />
            <div className="relative">
                <DataContainer limit={limit} data={data} blur={showLoading} type={type} />
                {showLoading && <LoadingContainer showEmptyData={false} />}
            </div>
            <ShowMoreButton loading={loading} hasMore={hasMore} limit={limit} isOrganization={isOrganization} onShowMore={handleShowMore} t={t} />
        </div>
    )
}

const DataContainer = React.memo(({ data, limit, blur, type }) => {
    const { tourOpen } = useTourContext()
    const isOrganization = type === 'organization'
    const schoolItem = isOrganization ? data?.organization : data?.school
    const maxValue = Math.max(schoolItem?.comfortable ?? 1, schoolItem?.uncomfortable ?? 1)
    const intervalNumbers = Math.ceil((maxValue + maxValue * 0.1) / (calculateCoefficient(maxValue) * STEP)) + 1

    const schoolData = isOrganization ? data?.organization : data?.school
    const total = schoolData?.comfortable + schoolData?.uncomfortable
    const schoolPercentage = useMemo(
        () => ({
            comfortable: calculatePercentage(schoolData?.comfortable, total),
            uncomfortable: calculatePercentage(schoolData?.uncomfortable, total),
        }),
        [schoolData?.comfortable, schoolData?.uncomfortable, total]
    )

    const dataItems = data[isOrganization ? 'institutions' : 'years']?.slice(0, limit) || []

    return (
        <div className={`mt-4 pb-8 ${blur && !tourOpen ? 'blur' : ''}`}>
            <div className="relative flex-1 z-0">
                <ChartGrid intervalNumbers={intervalNumbers} maxValue={maxValue} />
                <div className="w-full relative flex flex-col gap-4 mt-4" style={{ zIndex: 2 }}>
                    <SingleItem item={schoolItem} intervalNumbers={intervalNumbers} maxValue={maxValue} isClass={false} type={type} />
                    {dataItems.map((item, index) => (
                        <SingleItem
                            key={`${item.year || item.id}-${index}`}
                            item={item}
                            intervalNumbers={intervalNumbers}
                            initialOpen={index === 0 && tourOpen}
                            maxValue={maxValue}
                            index={index}
                            data={data}
                            schoolPercentage={schoolPercentage}
                            type={type}
                        />
                    ))}
                </div>
            </div>
        </div>
    )
})

const ChartGrid = React.memo(({ intervalNumbers, maxValue }) => (
    <div className="absolute h-full inset-0 flex flex-row justify-between" style={{ zIndex: 1 }}>
        <div className="flex-1 flex flex-row justify-between">
            {Array(intervalNumbers)
                .fill(0)
                .map((_, index) => (
                    <div key={index} className="h-full border-r border-dashed border-[#ECEBF8] relative">
                        <span className="absolute top-full mt-4 text-[14px] leading-5 text-[#77768D] font-medium" style={{ transform: 'translateX(-50%)' }}>
                            {index * calculateCoefficient(maxValue) * 10}
                        </span>
                    </div>
                ))}
        </div>
        <div className="w-[36px]" />
    </div>
))

const SingleItem = React.memo(({ item, intervalNumbers, initialOpen = false, maxValue, isClass = true, index, data, schoolPercentage, type }) => {
    const [open, setOpen] = useState(initialOpen)
    const { t } = useTranslation()
    const isOrganization = type === 'organization'

    const percentage_comfortable = Math.round((item?.comfortable / (intervalNumbers - 1)) * (100 / (calculateCoefficient(maxValue) * STEP)))
    const percentage_uncomfortable = Math.round((item?.uncomfortable / (intervalNumbers - 1)) * (100 / (calculateCoefficient(maxValue) * STEP)))
    const total = item?.comfortable + item?.uncomfortable
    const percentageComfortableNumber = calculatePercentage(item?.comfortable, total)
    const percentageUncomfortableNumber = calculatePercentage(item?.uncomfortable, total)

    const trendAnalysis = useMemo(() => {
        if (!isClass || !schoolPercentage) return null

        return calculateTrendComparison(percentageComfortableNumber, schoolPercentage.comfortable, TREND_THRESHOLD)
    }, [percentageComfortableNumber, schoolPercentage, isClass])

    const isLastItem = index + 1 === data?.yearsList?.length

    useEffect(() => {
        setOpen(initialOpen)
    }, [initialOpen])

    const getLabel = useCallback(() => {
        if (type === 'counsellor') {
            return isClass ? t('Year {{year}} average', { year: item?.year }) : t('School average')
        } else {
            return isClass ? t('school_average', { name: item?.institutionName }) : t('Organization average')
        }
    }, [type, isClass, item?.year, item?.institutionName, t])

    const handleToggle = useCallback(() => {
        setOpen((prev) => !prev)
    }, [])

    return (
        <div className={`flex flex-col gap-[10px] ${isLastItem ? '' : ' pb-3 border-b border-b-[#E8E0FF]'}`}>
            <div className="flex flex-col gap-[10px]" style={{ backgroundColor: isClass ? 'transparent' : 'white' }}>
                <ItemHeader isClass={isClass} item={item} type={type} getLabel={getLabel} trendAnalysis={trendAnalysis} />
                <ProgressSection
                    percentage_comfortable={percentage_comfortable}
                    percentage_uncomfortable={percentage_uncomfortable}
                    item={item}
                    percentageComfortableNumber={percentageComfortableNumber}
                    percentageUncomfortableNumber={percentageUncomfortableNumber}
                    isClass={isClass}
                    open={open}
                    onToggle={handleToggle}
                    schoolPercentage={schoolPercentage}
                    type={type}
                    isOrganization={isOrganization}
                />
            </div>
        </div>
    )
})

const ItemHeader = React.memo(({ isClass, item, type, getLabel, trendAnalysis }) => (
    <div className="flex flex-row items-center gap-2">
        <div className="min-w-7 max-w-7 h-7 rounded-full bg-[#B8B8D6] flex items-center justify-center">
            {isClass ? (
                <p className="text-[12px] leading-[20px] font-medium text-[#000000]">
                    {item?.year?.[0] ? `${type === 'counsellor' ? 'Y' : 'S'}${item.year[0]}` : type === 'counsellor' ? 'Y' : 'S'}
                </p>
            ) : (
                <SchoolAverageIcon />
            )}
        </div>
        <p className="text-[14px] leading-[20px] font-medium text-darkenPurple">{getLabel()}</p>
        {isClass && trendAnalysis && <TrendIndicator trendAnalysis={trendAnalysis} />}
    </div>
))

const TrendIndicator = React.memo(({ trendAnalysis }) => (
    <div className="flex-1 flex flex-row items-center gap-[6px]">
        <div className={`${!trendAnalysis.positiveTrend ? 'rotate-0' : 'rotate-180'} transition`}>
            <BigArrow color={!trendAnalysis.positiveTrend ? '#FC4C59' : '#6FCC87'} width={10} height={14} opacity={trendAnalysis.sameTrend ? 0 : 1} />
        </div>
        <p className="text-[14px] leading-[14px] font-medium" style={{ color: !trendAnalysis.positiveTrend ? '#FC4C59' : '#6FCC87' }}>
            {trendAnalysis.label}
        </p>
    </div>
))

const ProgressSection = React.memo(
    ({
        percentage_comfortable,
        percentage_uncomfortable,
        item,
        percentageComfortableNumber,
        percentageUncomfortableNumber,
        isClass,
        open,
        onToggle,
        schoolPercentage,
        type,
        isOrganization,
    }) => (
        <div className="flex-1 bg-[#F8F8FB] rounded-[4px] p-[2px]">
            <div className="flex-1 flex flex-row gap-[10px]">
                <div className="flex flex-col gap-[1px] h-[31px] flex-1">
                    <div className="flex-1">
                        <ProgressBarComfortable
                            frontColor="#6FCC8799"
                            restColor="#F8F8FB"
                            completed={percentage_comfortable}
                            value={item?.comfortable}
                            showPercentage
                            percentage={percentageComfortableNumber}
                        />
                    </div>
                    <div className="flex-1 py-[3px]">
                        <ProgressBarComfortable
                            height={4}
                            frontColor="#FC4C5999"
                            restColor="#F8F8FB"
                            completed={percentage_uncomfortable}
                            roundedLeft={1}
                            roundedRight={2}
                            value={item?.uncomfortable}
                            comfortable={false}
                            showPercentage
                            percentage={percentageUncomfortableNumber}
                        />
                    </div>
                </div>
                <div className="w-[26px] pt-[4px]">{isClass && <ExpandButton open={open} onClick={onToggle} width={24} height={24} />}</div>
            </div>
            <ExpandableContent
                open={open}
                item={item}
                isOrganization={isOrganization}
                schoolPercentage={schoolPercentage}
                percentageComfortableNumber={percentageComfortableNumber}
                percentageUncomfortableNumber={percentageUncomfortableNumber}
                type={type}
            />
        </div>
    )
)

const ExpandableContent = React.memo(({ open, item, isOrganization, schoolPercentage, percentageComfortableNumber, percentageUncomfortableNumber, type }) => (
    <div className={`overflow-hidden transition-all ${open ? 'max-h-[1000px] h-auto' : 'max-h-0'} ${open ? 'opacity-100 p-1' : 'opacity-0'}`}>
        <div className="flex flex-col gap-[6px]">
            {!isOrganization &&
                item?.classes?.map((classItem) => (
                    <SingleClassItem
                        key={classItem?.id}
                        classItem={classItem}
                        schoolPercentage={schoolPercentage}
                        yearPercentage={{ comfortable: percentageComfortableNumber, uncomfortable: percentageUncomfortableNumber }}
                        type={type}
                    />
                ))}
            {isOrganization &&
                item?.years?.map((classItem) => (
                    <SingleClassItem
                        key={classItem?.id}
                        classItem={classItem}
                        schoolPercentage={schoolPercentage}
                        yearPercentage={{ comfortable: percentageComfortableNumber, uncomfortable: percentageUncomfortableNumber }}
                        type={type}
                    />
                ))}
        </div>
    </div>
))

const SingleClassItem = React.memo(({ classItem, schoolPercentage, yearPercentage, type }) => {
    const { t } = useTranslation()
    const location = useLocation()
    const params = new URLSearchParams(location.search)
    const daysCode = params.get('days')

    const total = classItem?.comfortable + classItem?.uncomfortable
    const percentageComfortableNumber = calculatePercentage(classItem?.comfortable, total)

    const schoolTrend = useMemo(
        () => calculateTrendComparison(percentageComfortableNumber, schoolPercentage?.comfortable, TREND_THRESHOLD),
        [percentageComfortableNumber, schoolPercentage?.comfortable]
    )

    const yearTrend = useMemo(
        () => calculateTrendComparison(percentageComfortableNumber, yearPercentage?.comfortable, TREND_THRESHOLD),
        [percentageComfortableNumber, yearPercentage?.comfortable]
    )

    return (
        <div className="flex flex-row items-center gap-10 p-2 bg-white rounded-[4px]">
            <div className="flex flex-col gap-1 flex-1">
                <div className="flex flex-row items-center gap-4">
                    <p className="text-darkenPurple text-[13px] leading-[18px] font-medium">{classItem?.name}</p>
                    <TrendDisplay trend={schoolTrend} suffix="school avg." />
                    <TrendDisplay trend={yearTrend} suffix={`${type === 'counsellor' ? 'year' : 'organization'} avg.`} />
                </div>
                <div className="flex flex-col gap-[1px]">
                    <CounsellorWellbeingProgressBar comfortableEmotions={classItem?.comfortable} uncomfortableEmotions={classItem?.uncomfortable} positive />
                    <CounsellorWellbeingProgressBar comfortableEmotions={classItem?.comfortable} uncomfortableEmotions={classItem?.uncomfortable} />
                </div>
            </div>
            {type === 'counsellor' ? (
                <Link to={`/analytics/class?id=${classItem?.id}&days=${daysCode}`} className="flex items-center justify-center h-[36px] rounded-full bg-[#86D3FF33] px-4 gap-1">
                    <UserCardId />
                    <p className="text-[#413F5E] font-normal text-[14px] leading-5">{t('See Class Analytics')}</p>
                </Link>
            ) : (
                <div className="h-[36px] flex-1 px-4 gap-1 max-w-[222px]" />
            )}
        </div>
    )
})

const TrendDisplay = React.memo(({ trend, suffix }) => (
    <div className="flex flex-row items-center gap-2">
        <div className={`${!trend.positiveTrend ? 'rotate-0' : 'rotate-180'} transition`}>
            <BigArrow color={!trend.positiveTrend ? '#FC4C59' : '#6FCC87'} width={10} height={14} opacity={trend.sameTrend ? 0 : 1} />
        </div>
        <p className="text-[13px] leading-[13px] font-medium" style={{ color: !trend.positiveTrend ? '#FC4C59' : '#6FCC87' }}>
            {trend.sameTrend ? `same as ${suffix}` : `${trend.label.replace('average', suffix)}`}
        </p>
    </div>
))

const CounsellorWellbeingProgressBar = React.memo(({ comfortableEmotions, uncomfortableEmotions, positive = false, showPercentage = true }) => {
    const total = comfortableEmotions + uncomfortableEmotions
    const percentage = total !== 0 && positive ? Math.round((comfortableEmotions / total) * 100) : total !== 0 && !positive ? Math.round((uncomfortableEmotions / total) * 100) : 0

    return (
        <div className="w-full rounded-[10px] flex flex-row items-center gap-2">
            <div
                className="transition-all rounded-[2px] h-1"
                style={{
                    width: `${percentage || 0.5}%`,
                    backgroundColor: positive ? '#6FCC8799' : '#FC4C5999',
                }}
            />
            <p className="text-[10px] leading-[10px] font-medium text-nowrap" style={{ color: positive ? '#6FCC87' : '#FC4C59' }}>
                {positive ? comfortableEmotions : uncomfortableEmotions} {showPercentage && `(${percentage}%)`}
            </p>
        </div>
    )
})

export default WellbeingComparisonWidget
